import React, { useEffect, useState } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import Section from '../Section';
import Alert from '../Alert';
import EditSocialForm from './EditSocialForm';
import CustomSocialForm from './CustomSocialForm';
import LinktreeImport from './Linktree/LinktreeImport';
import RegisteredIcon from '../RegisteredIcon';
import { SOCIAL_PLATFORMS } from 'mayhem-icon-registry';

const PLATFORM_OPTIONS = [
    { key: 'custom', label: 'Custom social', icon: 'fa-solid fa-link' },
    ...SOCIAL_PLATFORMS
];

export default function SocialsManager({
    initialSocials = {},
    onChanged,
    wrapperElement = 'section',
}) {
    const { fetch, API_URL } = useAuth();
    const [socials, setSocials] = useState(initialSocials);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const [customSocial, setCustomSocial] = useState({
        label: '',
        icon: 'fa-solid fa-link',
        url: ''
    });
    const [editingSocial, setEditingSocial] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const successTimerRef = React.useRef(null);

    useEffect(() => () => clearTimeout(successTimerRef.current), []);

    useEffect(() => {
        let isMounted = true;

        const loadSocials = async () => {
            try {
                const response = await fetch(`${API_URL}/v1/seller/socials`, {
                    headers: {
                        'Accept': 'application/json',
                    },
                });
                if (!response.ok) {
                    if (isMounted) setError('Failed to load your socials. Please refresh.');
                    return;
                }
                const data = await response.json();
                if (isMounted && data.socials) {
                    setSocials(data.socials);
                }
            } catch (err) {
                console.error('Error loading socials:', err);
                if (isMounted) setError('Failed to load your socials. Please refresh.');
            }
        };

        loadSocials();

        return () => {
            isMounted = false;
        };
    }, [API_URL, reloadKey]);

    // The importer saves through its own endpoint, so re-read the socials it just wrote.
    const handleImported = () => {
        setReloadKey(key => key + 1);
        onChanged?.();
    };

    // Get currently configured socials (including custom ones)
    const configuredSocials = [
        // Standard socials
        ...Object.entries(socials)
            .filter(([key, value]) => key !== 'custom' && typeof value === 'string' && value.trim() !== '')
            .map(([key, url]) => {
                const platform = SOCIAL_PLATFORMS.find(p => p.key === key);
                return {
                    key,
                    url,
                    platform: platform || {
                        key,
                        label: key.charAt(0).toUpperCase() + key.slice(1),
                        icon: 'fa-solid fa-link',
                        prefix: ''
                    }
                };
            }),
        // Custom socials
        ...(Array.isArray(socials.custom) ? socials.custom : []).map(customSocial => ({
            key: customSocial.key,
            url: customSocial.url,
            platform: {
                key: customSocial.key,
                label: customSocial.label || customSocial.key,
                icon: customSocial.icon || 'fa-solid fa-link',
                prefix: '',
                isCustom: true
            }
        }))
    ];

    // Get available platforms (not yet configured)
    const availablePlatforms = SOCIAL_PLATFORMS.filter(
        platform => typeof socials[platform.key] !== 'string' || socials[platform.key].trim() === ''
    );

    const handlePlatformSelection = (platform) => {
        setSelectedPlatform(platform);

        if (platform !== 'custom') {
            setEditingSocial({
                key: platform,
                url: '',
                platform: SOCIAL_PLATFORMS.find(p => p.key === platform) || {
                    key: platform,
                    label: platform.charAt(0).toUpperCase() + platform.slice(1),
                    icon: 'fa-solid fa-link',
                    prefix: ''
                },
                icon: 'fa-solid fa-link'
            });
        }
    };

    const handleAddCustom = async (formValues) => {
        const label = typeof formValues.label === 'string' ? formValues.label.trim() : '';
        const url = typeof formValues.url === 'string' ? formValues.url.trim() : '';
        const icon = typeof formValues.icon === 'string' && formValues.icon.trim()
            ? formValues.icon.trim()
            : 'fa-solid fa-link';

        if (!label) {
            setError('Please provide a label for the custom social');
            return;
        }
        if (!url) {
            setError('Please provide a URL for the custom social');
            return;
        }

        // Generate a unique key based on label
        const baseKey = label.toLowerCase().replace(/[^a-z0-9]/g, '') || 'custom';
        let key = baseKey;
        let counter = 1;
        
        // Ensure key is unique by checking existing socials and custom array
        const existingCustomSocials = Array.isArray(socials.custom) ? socials.custom : [];
        while ((typeof socials[key] === 'string') || existingCustomSocials.some(c => c.key === key)) {
            key = `${baseKey}${counter}`;
            counter++;
        }

        const updatedSocials = {
            ...socials,
            custom: [
                ...existingCustomSocials,
                {
                    key,
                    label,
                    icon,
                    url
                }
            ]
        };

        const savedSocials = await saveSocials(updatedSocials);
        if (!savedSocials) return;

        setCustomSocial({
            label: '',
            icon: 'fa-solid fa-link',
            url: ''
        });
        setEditingSocial(null);
        setSelectedPlatform('');
    };

    const handleEdit = (social) => {
        setEditingSocial(social);
    };

    const handleSaveEdit = async (updatedSocial) => {
        if (!updatedSocial) return;

        let updatedSocials = { ...socials };

        if (updatedSocial.platform.isCustom) {
            // Update custom social in the custom array
            updatedSocials.custom = (Array.isArray(socials.custom) ? socials.custom : []).map(customSocial =>
                customSocial.key === updatedSocial.key
                    ? { ...customSocial, url: updatedSocial.url }
                    : customSocial
            );
        } else {
            // Update standard social
            updatedSocials[updatedSocial.key] = updatedSocial.url;
        }

        const savedSocials = await saveSocials(updatedSocials);
        if (savedSocials) {
            setEditingSocial(null);
            setSelectedPlatform('');
        }
    };

    const handleCancelEdit = () => {
        setEditingSocial(null);
        setSelectedPlatform('');
    };

    const handleRemove = async (key) => {
        if (!window.confirm('Remove this social link?')) return;
        let updatedSocials = { ...socials };
        
        // Check if it's a custom social
        const existingCustomSocials = Array.isArray(socials.custom) ? socials.custom : [];
        const isCustom = existingCustomSocials.some(c => c.key === key);
        
        if (isCustom) {
            // Remove from custom array
            updatedSocials.custom = existingCustomSocials.filter(c => c.key !== key);
        } else {
            // Remove standard social
            delete updatedSocials[key];
        }
        
        await saveSocials(updatedSocials);
    };

    const saveSocials = async (updatedSocials) => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(`${API_URL}/v1/seller/socials`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(updatedSocials)
            });
            const data = await response.json().catch(() => ({}));

            if (response.ok) {
                const savedSocials = data.socials || updatedSocials;
                setSocials(savedSocials);
                setSuccess('Socials updated successfully!');
                onChanged?.();
                clearTimeout(successTimerRef.current);
                successTimerRef.current = setTimeout(() => setSuccess(''), 3000);
                return savedSocials;
            } else {
                throw new Error(data.error || 'Failed to save socials');
            }
        } catch (err) {
            setError('Failed to save socials. Please try again.');
            console.error('Error saving socials:', err);
            return null;
        } finally {
            setLoading(false);
        }
    };

    return (
        <Section
            title="Social Media Links"
            collapsible={false}
            wrapperElement={wrapperElement}
        >
            {success && <Alert type="success">{success}</Alert>}
            {error && <Alert type="error">{error}</Alert>}
            <p>These are shown as icons across the top of your page. For best results, make sure you enter the full links to your profile starting with "https://" below.</p>
            <LinktreeImport onImported={handleImported} />

            {/* Current Socials */}
            <div className="current-socials">
                {configuredSocials.length === 0 ? (
                    <p>No social links configured yet. Add your first social link below!</p>
                ) : (
                    <div className="socials-list">
                        {configuredSocials.map((social) => (
                            <div key={social.key} className="social-item">
                                <div className="social-info">
                                    <RegisteredIcon value={social.platform.icon} />
                                    <span className="social-label">{social.platform.label}</span>
                                    <span className="social-url" title={social.url}>{social.url.length > 50 ? social.url.slice(0, 50) + '…' : social.url}</span>
                                </div>
                                <div className="social-actions">
                                    <button 
                                        type="button" 
                                        onClick={() => handleEdit(social)}
                                        className="button secondary small"
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => handleRemove(social.key)}
                                        className="button danger small"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Edit Form */}
            <div className="add-social-form">
                <div className="group">
                    <label>Add a Social Link</label>
                    <select
                        value={selectedPlatform}
                        onChange={e => handlePlatformSelection(e.target.value)}
                    >
                        <option value="">Select a platform...</option>
                        {PLATFORM_OPTIONS
                            .filter(option =>
                                option.key === 'custom' ||
                                availablePlatforms.some(p => p.key === option.key)
                            )
                            .map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                    </select>
                </div>
            </div>

            {editingSocial && (
                <EditSocialForm
                    key={editingSocial.key}
                    social={editingSocial}
                    loading={loading}
                    onSave={handleSaveEdit}
                    onCancel={handleCancelEdit}
                />
            )}

            {selectedPlatform === 'custom' ? (
                <CustomSocialForm
                    values={customSocial}
                    loading={loading}
                    onChange={(newValues) => setCustomSocial(newValues)}
                    onSubmit={handleAddCustom}
                    onCancel={() => {
                        setSelectedPlatform('');
                        setCustomSocial({ label: '', icon: 'fa-solid fa-link', url: '' });
                    }}
                />
            ) : null}

            <style>{`
                .current-socials {
                    margin-bottom: 2rem;
                }
                
                .socials-list {
                    border: 1px solid #ddd;
                    border-radius: 4px;
                }
                
                .social-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-bottom: 1px solid #eee;
                }
                
                .social-item:last-child {
                    border-bottom: none;
                }
                
                .social-info {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex: 1;
                }
                
                .social-info i {
                    width: 20px;
                    text-align: center;
                    color: #666;
                }
                
                .social-label {
                    font-weight: 500;
                    min-width: 70px;
                }
                
                .social-url {
                    color: #666;
                    font-size: 0.8em;
                    word-break: break-all;
                }
                
                .social-actions {
                    display: flex;
                    gap: 0.5rem;
                }
                
                @media (max-width: 600px) {
                    .social-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.75rem;
                    }
                    
                    .social-actions {
                        width: 100%;
                        justify-content: flex-end;
                    }
                }
                
                .edit-social-form,
                .add-social-form,
                .custom-social-form {
                    background: #f9f9f9;
                    padding: 1.5rem;
                    border-radius: 4px;
                    margin: 1rem 0;
                }

                [data-theme="dark"] .socials-list {
                    border-color: #3a463e;
                }

                [data-theme="dark"] .social-item {
                    border-bottom-color: #2c3a33;
                }

                [data-theme="dark"] .social-info i,
                [data-theme="dark"] .social-url {
                    color: #9fb0a6;
                }

                [data-theme="dark"] .edit-social-form,
                [data-theme="dark"] .add-social-form,
                [data-theme="dark"] .custom-social-form {
                    background: #222f28;
                }

                [data-theme="dark"] .icon-preview {
                    background: #1c2620;
                    border-color: #3a463e;
                }

                [data-theme="dark"] .icon-preview i {
                    color: #9fb0a6;
                }
                
                .radio-group {
                    display: flex;
                    gap: 1rem;
                }
                
                .radio-group label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-weight: normal;
                }
                
                .icon-preview {
                    margin-top: 0.5rem;
                    padding: 0.5rem;
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-weight: 500;
                }
                
                .icon-preview i {
                    margin-right: 0.5rem;
                    color: #666;
                }
                
                .btn-sm {
                    padding: 0.25rem 0.75rem;
                    font-size: 0.875rem;
                }
                
                .btn-danger {
                    background-color: #dc3545;
                    color: white;
                }
                
                .btn-danger:hover {
                    background-color: #c82333;
                }
            `}</style>
        </Section>
    );
}
